import React, { useState } from "react";
import { toast } from "react-toastify";
import { buildPlan, savePlan, fetchShopifyProducts } from "../../services/strategyService";
import ConnectPlatformBanner from "./components/ConnectPlatformBanner";
import StrategySteps from "./StrategySteps";

const CATEGORIES = [["beauty", "Beauté"], ["fashion", "Mode"], ["fitness", "Fitness"], ["tech", "Tech"], ["home", "Maison"], ["food", "Food"], ["jewelry", "Bijoux"], ["kids", "Enfants"], ["pets", "Animaux"], ["other", "Autre"]];
const PLATFORMS = [["meta", "Meta"], ["tiktok", "TikTok"], ["snapchat", "Snapchat"], ["google", "Google"]];
const EXP = [["beginner", "Débutant"], ["intermediate", "Intermédiaire"], ["advanced", "Avancé"]];

const Pills = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([k, l]) => (
      <button key={k} onClick={() => onChange(k)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${value === k ? "bg-[#221C51] text-white border-[#221C51]" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>{l}</button>
    ))}
  </div>
);

// (le rendu manuel des adsets a été remplacé par le composant intelligent StrategySteps)

const StrategyLauncher = () => {
  const [draft, setDraft] = useState({ productName: "", category: "beauty", platform: "meta", dailyBudget: 30, experience: "beginner" });
  const [plan, setPlan] = useState(null);
  const [saved, setSaved] = useState(null); // { planId, steps, summary } — le plan persisté
  const [loading, setLoading] = useState(false);
  // Intégration Shopify : produit source (nom + image réutilisée dans les pubs).
  const [shopProduct, setShopProduct] = useState(null);
  const [products, setProducts] = useState(null); // null = pas encore chargé
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const openPicker = async () => {
    setPickerOpen(true);
    if (products) return;
    setLoadingProducts(true);
    try { setProducts(await fetchShopifyProducts()); }
    catch { toast.error("Connectez Shopify pour importer vos produits."); setProducts([]); }
    finally { setLoadingProducts(false); }
  };
  const pickProduct = (p) => {
    setShopProduct(p);
    set("productName", p.title || "");
    setPickerOpen(false);
  };
  const generate = async () => {
    if (!draft.productName.trim()) return toast.warn("Nommez votre produit.");
    setLoading(true);
    setPlan(null); setSaved(null);
    try {
      const payload = { ...draft, dailyBudget: Number(draft.dailyBudget) || 30 };
      const p = await buildPlan(payload);
      setPlan(p);
      // Persiste le plan en étapes → alimente le composant intelligent.
      // Si un produit Shopify est source, son image devient la créative des adsets.
      try {
        const s = await savePlan({
          plan: p, ...payload,
          creativeUrls: shopProduct?.image_url ? [shopProduct.image_url] : undefined,
          shopifyProductId: shopProduct ? String(shopProduct.id) : undefined,
        });
        setSaved(s);
      } catch { /* le plan reste consultable même si la sauvegarde échoue */ }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Génération impossible.");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-2 max-w-5xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#221C51]">Stratégie de lancement</h2>
        <p className="text-sm text-gray-400 mt-0.5">Un plan média exact, pas-à-pas, pour lancer vos pubs e-commerce.</p>
      </div>

      <ConnectPlatformBanner moduleLabel="la stratégie" accent="amber" />

      {/* Brief */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-[#221C51]">1 · Votre produit</p>
          <button onClick={openPicker} className="text-xs font-semibold text-[#96BF48] border border-[#96BF48]/40 rounded-lg px-3 py-1.5 hover:bg-[#96BF48]/10">
            🛍️ Importer depuis Shopify
          </button>
        </div>

        {/* Produit Shopify sélectionné */}
        {shopProduct && (
          <div className="flex items-center gap-3 bg-[#96BF48]/8 border border-[#96BF48]/30 rounded-xl p-2.5 mb-3">
            {shopProduct.image_url && <img src={shopProduct.image_url} alt={shopProduct.title} className="w-12 h-12 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#221C51] truncate">{shopProduct.title}</p>
              <p className="text-xs text-gray-400">{shopProduct.price ? `${shopProduct.price} · ` : ""}image utilisée pour les créas</p>
            </div>
            <button onClick={() => setShopProduct(null)} className="text-xs text-gray-400 hover:text-gray-600 px-2">Retirer</button>
          </div>
        )}

        <input value={draft.productName} onChange={(e) => set("productName", e.target.value)} placeholder="Sérum Vitamine C"
          className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51] mb-4" />
        <div className="grid md:grid-cols-2 gap-4">
          <div><p className="text-xs font-semibold text-gray-500 mb-2">Catégorie</p><Pills options={CATEGORIES} value={draft.category} onChange={(v) => set("category", v)} /></div>
          <div><p className="text-xs font-semibold text-gray-500 mb-2">Plateforme</p><Pills options={PLATFORMS} value={draft.platform} onChange={(v) => set("platform", v)} /></div>
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">Budget quotidien (€)</p>
            <input type="number" value={draft.dailyBudget} onChange={(e) => set("dailyBudget", e.target.value)} className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg" />
          </div>
          <div><p className="text-xs font-semibold text-gray-500 mb-2">Niveau</p><Pills options={EXP} value={draft.experience} onChange={(v) => set("experience", v)} /></div>
        </div>
        <button onClick={generate} disabled={loading} className="mt-5 px-6 py-2.5 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2e265e] disabled:opacity-50">
          {loading ? "Génération…" : "Générer mon plan média"}
        </button>
      </div>

      {plan && (
        <div className="space-y-5">
          {/* Résumé + checklist */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between">
              <p className="font-bold text-[#221C51]">Plan · {plan.productName}</p>
              <span className="text-xs font-semibold bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full">CPA cible {plan.targetCpa} €</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{plan.structureAdvice}</p>
            <ul className="mt-3 grid md:grid-cols-2 gap-1.5">
              {plan.checklist.map((c, i) => <li key={i} className="text-sm text-gray-600 flex gap-2"><span className="text-green-500">✓</span>{c}</li>)}
            </ul>
          </div>

          {/* Composant intelligent — résumé des étapes + validation + lancement batch */}
          {saved ? (
            <StrategySteps
              planId={saved.planId}
              steps={saved.steps}
              summary={saved.summary}
              onChange={(res) => setSaved((s) => ({ ...s, steps: res.steps, summary: res.summary }))}
            />
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-700">
              Plan généré. La sauvegarde des étapes est momentanément indisponible — reconnectez-vous pour activer le lancement en batch.
            </div>
          )}

          {/* A/B testing */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="font-bold text-[#221C51] mb-3">A/B Testing — une variable à la fois</p>
            <div className="grid md:grid-cols-2 gap-3">
              {plan.abTests.map((ab, i) => (
                <div key={i} className="border-l-4 border-sky-400 pl-4">
                  <p className="font-semibold text-[#221C51] text-sm">{ab.variable}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {ab.variants.map((v, j) => <span key={j} className="text-[11px] bg-sky-50 text-sky-700 px-2 py-0.5 rounded-full font-medium">{String.fromCharCode(65 + j)} · {v}</span>)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{ab.rule}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Règles */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="font-bold text-[#221C51] mb-3">Règles de décision (kill / scale)</p>
            <ul className="space-y-1.5">
              {plan.killRules.map((r, i) => (
                <li key={i} className="text-sm flex items-center gap-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${r.action === "kill" ? "bg-red-100 text-red-700" : r.action === "scale" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{r.action}</span>
                  <span className="text-gray-600">{r.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Modale — sélection d'un produit Shopify */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPickerOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <p className="font-bold text-[#221C51]">Choisir un produit Shopify</p>
              <button onClick={() => setPickerOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="p-4 overflow-y-auto">
              {loadingProducts ? (
                <div className="flex justify-center py-12"><div className="w-7 h-7 border-4 border-[#221C51]/20 border-t-[#221C51] rounded-full animate-spin" /></div>
              ) : !products || products.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-10">Aucun produit — connectez votre boutique dans « Plateformes ».</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {products.map((p) => (
                    <button key={p.id} onClick={() => pickProduct(p)}
                      className="text-left rounded-xl border border-gray-200 hover:border-[#96BF48] hover:shadow-sm overflow-hidden transition">
                      {p.image_url
                        ? <img src={p.image_url} alt={p.title} className="w-full aspect-square object-cover" />
                        : <div className="w-full aspect-square bg-gray-100 flex items-center justify-center text-gray-300 text-2xl">🛍️</div>}
                      <div className="p-2">
                        <p className="text-xs font-semibold text-[#221C51] truncate">{p.title}</p>
                        {p.price && <p className="text-[11px] text-gray-400">{p.price}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyLauncher;
